import * as yup from "yup";
import { Formik, Form } from "formik";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Route, useNavigate } from "react-router-dom";
import { useLoginMutation } from "../../../features/api/Api";
import { useAppDispatch } from "../../../app/hooks";
import { setCredentials } from "../../../features/auth/AuthSlice";
import Swal from "sweetalert2";
import { email_regex } from "../../../utils/regex";
import {
  Container,
  Grid,
  Button,
  TextField,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import RecaptchaInput from "../../../components/inputs/RecaptchaInput";
import VerifyDialog from "./common/VerifyDialog";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ChangePasswordModal from "./common/ChangePasswordModal";

const languages = [
  {
    code: "tr",
    name: "Türkçe",
    country_code: "tr",
  },
  {
    code: "en",
    name: "English",
    country_code: "gb",
  },
];

interface ILogin {
  email: string;
  password: string;
  recaptcha: any;
  customerCode: string;
}

export const Login: FC = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [t, i18next] = useTranslation();
  const [login, { isLoading }] = useLoginMutation();
  const history = useNavigate();
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [email, setEmail] = useState("");
  const [customerCode, setCustomerCode] = useState("");

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (code: any) => {
    setAnchorEl(null);
    i18next.changeLanguage(code);
  };

  const loginForm: ILogin = {
    email: "",
    password: "",
    recaptcha: "",
    customerCode: "",
  };

  const validationSchema = yup.object({
    //recaptcha: yup.string().required("Zorunlu alanı doldurun."),
    email: yup
      .string()
      .email(t("resources.users.login.valid.valid_email"))
      .required(t("resources.users.login.valid.required_email"))
      .test(
        t("resources.users.login.valid.not_valid_email"),
        t("resources.users.login.valid.not_valid_email"),
        function () {
          return email_regex.test(this.parent.email) ? false : true;
        }
      ),

    password: yup
      .string()
      .min(
        6,
        ({ min }) =>
          `${min} ${t("resources.users.login.valid.error_password_length")}`
      )
      .required(t("resources.users.login.valid.error_password")),
    customerCode: yup
      .string()
      .matches(/^[0-9]+$/, "Sadece rakam giriniz.")
      .required(t("Lütfen müşteri kodu giriniz."))
      .nullable(),
  });

  const handleSubmit = async (values: any) => {
    const email = values.email;
    const password = values.password;
    const customerCode = values.customerCode;
    const clientId = "Bayi";

    login({
      email,
      password,
      customerCode,
      clientId,
    })
      .unwrap()
      .then((data: any) => {
        if (
          data.isSuccessful &&
          data.data.result === false &&
          data.data.requiresPasswordChange === true
        ) {
          console.log("şifre değiştir");
          setEmail(email);

          setOpenChangePasswordModal(true);
          return;
        } else if (data.isSuccessful && typeof data.data.result === "boolean") {
          setOpenModal(true);
          setPhoneNumber(data.data.phoneNumber);
        } else if (data.isSuccessful && typeof data.data === "object") {
          dispatch(setCredentials(data.data.accessToken));
          history("/");
        } else if (data.statusCode === 423 && data.data?.message) {
          // Show the Swal alert with the message if account is locked
          Swal.fire({
            icon: "error",
            title: "Hesabınız kilitlendi",
            text: data.data.message, // Display the message
            showCancelButton: false, // Disable the "Okay" button
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Değerli Müşterimiz,",
          text: error.data.errors[0],
        });
      });
  };

  return (
    <div className={`${process.env.REACT_APP_AUTH_CLASSNAME}`}>
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "100vh" }}
        >
          <Grid item md={5}>
            <div className="card card-login">
              <>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  style={{ marginBottom: "30px" }}
                >
                  <div>
                    <img src="/img/logo.svg" alt="Fixity" />
                  </div>
                </Grid>
                <Formik
                  initialValues={loginForm}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  onChange={() => {}}
                >
                  {({ values, errors, touched, handleChange }) => (
                    <Form>
                      <>
                        <TextField
                          fullWidth
                          id="customerCode"
                          name="customerCode"
                          label="Bayi Kodu"
                          value={values.customerCode}
                          onChange={handleChange}
                          error={
                            touched.customerCode && Boolean(errors.customerCode)
                          }
                          helperText={
                            touched.customerCode && errors.customerCode
                          }
                          style={{ marginBottom: "15px" }}
                        />
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          label={t("resources.users.login.email")}
                          value={values.email}
                          onChange={handleChange}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          style={{ marginBottom: "15px" }}
                        />
                        <TextField
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          label={t("resources.users.login.password")}
                          value={values.password}
                          onChange={handleChange}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          style={{ marginBottom: "30px" }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ marginRight: "5px" }}
                              >
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Grid container>
                          <Grid item md={12} textAlign="center">
                            <RecaptchaInput name="recaptcha" />
                          </Grid>
                        </Grid>

                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          style={{ marginBottom: "30px" }}
                        >
                          {/* <Grid item>
                              <FormControlLabel
                                label={t("resources.users.login.remember_me")}
                                control={
                                  <Checkbox
                                    name="remember_me"
                                    className="form-control-checkbox"
                                  />
                                }
                              />
                            </Grid> */}
                          <Grid item>
                            <Link to="/forgot">
                              {t("resources.users.login.forgot_password")}
                            </Link>
                          </Grid>
                        </Grid>
                        <Box sx={{ mx: "auto", width: 200 }}>
                          {!isLoading ? (
                            <Button
                              className="btn btn-default"
                              type="submit"
                              sx={{
                                mx: "auto",
                                width: 200,
                                textTransform: "none",
                              }}
                            >
                              {t("resources.users.login.login_button")}
                            </Button>
                          ) : (
                            <CircularProgress />
                          )}
                        </Box>
                      </>
                      {openModal ? (
                        <VerifyDialog
                          open={openModal}
                          message={message}
                          phoneNumber={phoneNumber}
                        />
                      ) : null}

                      {openChangePasswordModal ? (
                        <ChangePasswordModal
                          open={openChangePasswordModal}
                          onClose={() => setOpenChangePasswordModal(false)}
                          email={email}
                          customerCode={values.customerCode}
                        />
                      ) : null}
                    </Form>
                  )}
                </Formik>
              </>
            </div>
            <div
              className="card-callcenter"
              style={{ backgroundImage: "url(/img/icon_callcenter.svg)" }}
            >
              <h1>7 / 24 Müşteri Destek</h1>
              <h5>0850 290 0 290</h5>
            </div>
          </Grid>
        </Grid>
      </Container>
      {/* <MainModal /> */}
    </div>
  );
};

export default Login;
